<template>
  <q-сard bordered class="column full-height full-width">
    <q-form @submit.prevent="update">
      <q-card-section horizontal class="items-center justify-between q-gutter-md">
        <q-card-section>
          <div class="text-h6">Обновить задачу {{ task?.code }}</div>
        </q-card-section>
        <q-card-section>
          <q-btn icon="delete" round color="negative" size="10px" @click="deleteTask" />

        </q-card-section>

      </q-card-section>
      <q-separator />
      <q-card-section :horizontal="!$q.screen.xs">
        <q-card-section class="col-6">
          <div class="text-subtitle2">Параметры задачи</div>
          <q-input v-model="newTask.name" label="Имя задачи" :rules="[requiredRule]" />
          <q-input v-model="newTask.description" type="textarea" label="Описание" />
          <q-input v-model.number="newTask.photoCount" type="number" :rules="[requiredRule]" label="Количество фото" />
          <q-input v-model.number="newTask.priority" type="number" :rules="[requiredRule]" label="Приоритет" />
          <q-select use-chips multiple v-model="taskLabels" clearable :loading="isLabelLoading" :options="labels"
            :rules="[requiredRule]" option-label="name" option-value="id" label="Ярлыки" emit-value behavior="menu"
            map-options>
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-italic text-grey">
                  Нет доступных ярлыков
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select use-chips multiple v-model="performers" clearable :loading="isUserLoading" :options="performerUsers"
            :rules="[requiredRule, notEmptyRule]" :option-label="(user) => `${user.username} (${user.email})`"
            option-value="email" label="Исполнители" emit-value behavior="menu" map-options />
          <q-checkbox v-model="newTask.geo" label="Закрыть галлерею" class="q-my-sm" />

        </q-card-section>
        <q-card-section class="col-6" v-if="task?.type.propertyDescription.length > 0">
          <div class="text-subtitle2">Дополнительные параметры</div>
          <template v-for="property in newTask?.properties">
            <template v-if="property.type === 'STRING'">
              <q-input v-model="property.value" :label="property.name" :rules="[requiredRule]" />
            </template>
            <template v-if="property.type === 'NUMBER'">
              <q-input v-model.number="property.value" :label="property.name" type="number" :rules="[requiredRule]" />
            </template>
            <template v-if="property.type === 'DATE'">
              <q-input v-model="property.value" :label="property.name" :rules="[requiredRule]">
                <template v-slot:prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                      <q-date v-model="property.value" mask="YYYY-MM-DD">
                        <div class="row items-center justify-end">
                          <q-btn v-close-popup label="Закрыть" color="primary" flat />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </template>
            <template v-if="property.type === 'DATETIME'">
              <q-input v-model="property.value" :label="property.name" :rules="[requiredRule]">
                <template v-slot:prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                      <q-date v-model="property.value" mask="YYYY-MM-DD HH:mm">
                        <div class="row items-center justify-end">
                          <q-btn v-close-popup label="Закрыть" color="primary" flat />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>

                <template v-slot:append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                      <q-time v-model="property.value" mask="YYYY-MM-DD HH:mm" format24h>
                        <div class="row items-center justify-end">
                          <q-btn v-close-popup label="Закрыть" color="primary" flat />
                        </div>
                      </q-time>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </template>
          </template>
        </q-card-section>
        <q-card-section class="col-6" v-else>
          <div class="text-subtitle2">Дополнительные параметры отсуствуют</div>
        </q-card-section>
      </q-card-section>
      <q-card-actions>
        <q-btn no-caps label="Обновить" :disable="isTaskLoading" type="submit" color="primary" />
      </q-card-actions>
    </q-form>
  </q-сard>
</template>

<script setup lang="ts">
import { useProjectLabels, useProjectUsers } from "../../composables/project";
import { useRoute, useRouter } from "vue-router";
import { computed, reactive, ref, watch } from "vue";
import {
  useDeleteTask,
  useAddTaskLabels,
  useAddTaskPerformers,
  useTask,
  useUpdateTask
} from "../../composables/task";
import { requiredRule, notEmptyRule } from "../../util/rules";
import { UpdateTask } from "../../models/task";
import { useDayJs } from "../../composables/dayjs";
import { useQuasar } from "quasar";
import { useEventBus } from "@vueuse/core";
import { taskKey } from "../../util/events";
import { priorityOptions } from "../../util/state";

const route = useRoute()
const router = useRouter()
const dayjs = useDayJs()
const $q = useQuasar()

const newTask = reactive<UpdateTask>({
  name: "",
  description: "",
  priority: 0,
  properties: [],
  photoCount: 1,
  geo: false,
})

const projectId = computed(() => Number(route.params.projectId))
const taskId = computed(() => Number(route.params.taskId))
const taskEvent = useEventBus(taskKey)

const { data: labels, isFetching: isLabelLoading } = useProjectLabels(projectId)
const { data: task, isFetching: isTaskLoading } = useTask(projectId, taskId)
const { data: users, isFetching: isUserLoading } = useProjectUsers(projectId)

const performerUsers = computed(() => users.value?.filter(u => u.user.active && u.permission !== "READ").map(u => u.user) ?? [])
const taskLabels = ref<number[]>([])
const performers = ref<string[]>([])

watch(task, val => {
  newTask.geo = val?.geo ?? false
  newTask.photoCount = val?.photoCount ?? 1
  newTask.description = val?.description ?? ''
  newTask.priority = val?.priority ?? 0
  newTask.name = val?.name ?? ''
  performers.value = val?.assigned.map(v => v.email) ?? []
  taskLabels.value = val?.labels.map(v => v.id) ?? []
})

watch(task, val => {
  newTask.properties = val?.type.propertyDescription.map(d => ({
    ...d,
    value: task.value?.properties.find(v => v.code === d.code)?.value
  })) ?? []
})

const { execute: executeUpdate } = useUpdateTask(projectId, taskId, newTask)
const { execute: executeDelete } = useDeleteTask(projectId, taskId)
const { execute: executeLabels } = useAddTaskLabels(projectId, taskId, taskLabels)
const { execute: executePerformers } = useAddTaskPerformers(projectId, taskId, performers)

async function update() {
  await executeUpdate(true)
  await executeLabels(true)
  await executePerformers(true)
  taskEvent.emit('refresh')
  await router.push(`/projects/${projectId.value}/tasks/${taskId.value}`)
}
async function deleteTask() {
  $q.dialog({
    title: 'Удаление',
    message: 'Вы уверены что хотите удалить эту задачу?',
    cancel: true,
    persistent: true
  }).onOk(async data => {
    await executeDelete()
    taskEvent.emit('refresh')
    await router.push(`/projects/${projectId.value}/tasks`)

  })

}

</script>

<style scoped></style>